import React, { useContext, useEffect } from 'react';
import { Wallet, ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react';
import { WalletContext } from '../../context/WalletContext';

const WalletAndRewards = () => {
    const { balance, transactions, loading, refreshWallet } = useContext(WalletContext);

    useEffect(() => {
        refreshWallet();
    }, [refreshWallet]);

    const getTransactionTypeBadge = (txType) => {
        // e.g. Credit, Debit
        if (txType === 'Credit') {
            return <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20">Earned</span>;
        }
        return <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">Spent</span>;
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Wallet size={28} className="text-lime-400" />
                    <span>My Coin Wallet</span>
                </h2>
                
                <button 
                    onClick={refreshWallet} 
                    disabled={loading}
                    className="p-2 rounded-lg bg-[#222] border border-gray-800 text-gray-400 hover:text-white disabled:opacity-55 transition-colors cursor-pointer"
                    title="Refresh balance"
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Current Balance Card */}
            <div className="bg-gradient-to-br from-[#1c2912] to-[#0f140a] p-6 rounded-2xl mb-8 border border-[#6CD800]/25 shadow-lg shadow-[#6CD800]/5 flex justify-between items-center">
                <div>
                    <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Available Balance</h3>
                    <p className="text-4xl font-black text-white tracking-tight mt-1">
                        🪙 {balance.toLocaleString()}
                    </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#6CD800]/10 border border-[#6CD800]/20 flex items-center justify-center text-[#6CD800]">
                    <Wallet size={24} />
                </div>
            </div>

            {/* Transaction History Section */}
            <div>
                <h3 className="text-base font-bold text-white uppercase tracking-wider mb-4">Transaction History</h3>
                <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                    {loading && transactions.length === 0 ? (
                        <div className="text-center py-10">
                            <RefreshCw size={24} className="animate-spin text-gray-500 mx-auto" />
                            <p className="text-xs text-gray-500 mt-2">Loading transactions...</p>
                        </div>
                    ) : transactions.length > 0 ? (
                        transactions.map(tx => {
                            const isCredit = tx.amount > 0;
                            return (
                                <div 
                                    key={tx.id} 
                                    className="flex justify-between items-center bg-[#141414] border border-gray-850 p-4 rounded-xl transition-all hover:bg-[#1a1a1a] hover:border-gray-800"
                                >
                                    <div className="flex items-center gap-4">
                                        {isCredit ? (
                                            <ArrowUpCircle size={22} className="text-green-400" />
                                        ) : (
                                            <ArrowDownCircle size={22} className="text-red-400" />
                                        )}
                                        <div>
                                            <p className="font-bold text-white text-xs">{tx.note || 'Wallet Transaction'}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] text-gray-550">
                                                    {new Date(tx.createdAt).toLocaleString()}
                                                </span>
                                                {getTransactionTypeBadge(tx.type)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`text-sm font-black tracking-wide ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
                                        {isCredit ? `+🪙 ${tx.amount}` : `-🪙 ${Math.abs(tx.amount)}`}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-10 border border-dashed border-gray-800 rounded-2xl">
                            <p className="text-xs text-gray-500">You have no coin transactions yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WalletAndRewards;
